// 1:193:/Users/rajaishwary/dev/citymall/partner-db/src/screens/PlanDescription/index.tsx
import React, { useMemo, useCallback } from 'react';
import {
    View,
    ScrollView,
    Dimensions,
    StyleSheet,
    ActivityIndicator,
    Text,
} from 'react-native';
import HTML from 'react-native-render-html';
import { useRoute, useNavigation, useFocusEffect } from '@react-navigation/native';
import HeaderWithBackAndTitle from '@components/HeaderWithBackAndTitle';
import { ScaledSheet } from '@utils/scale';

// Get the default width for the HTML renderer (screen width minus any safe area padding)
const { width: deviceWidth } = Dimensions.get('window');

/**
 * PlanDescription Component
 * Renders HTML content safely in React Native using react-native-render-html.
 * Optimized for performance with proper scrolling and loading states.
 * 
 * @param html - The HTML string to render
 * @param [style] - Optional style for the wrapper View
 * @param [contentWidth=deviceWidth] - Width for the HTML content (defaults to device width)
 * @param [other props] - Additional props passed to the underlying HTML component for customization
 */
const PlanDescription: React.FC<any> = () => {
    const navigation = useNavigation();
    const route = useRoute();
    // @ts-ignore
    const htmlContent = route.params?.nav_value || '';

    // Memoized tags styles for better performance
    const tagsStyles = useMemo(() => ({
        p: {
            marginBottom: 12,
            fontSize: 16,
            lineHeight: 24,
        },
        h1: {
            fontSize: 24,
            fontWeight: 'bold',
            marginBottom: 16,
            marginTop: 16,
            textAlign: 'center',
        },
        h2: {
            fontSize: 20,
            fontWeight: '600',
            marginBottom: 12,
            marginTop: 12,
        },
        a: {
            color: '#007AFF',
            textDecorationLine: 'underline',
        },
        img: {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 8,
        },
        ul: {
            marginBottom: 12,
            paddingLeft: 20,
        },
        ol: {
            marginBottom: 12,
            paddingLeft: 20,
        },
        li: {
            marginBottom: 4,
        },
        strong: {
            fontWeight: 'bold',
        },
        em: {
            fontStyle: 'italic',
        },
        blockquote: {
            borderLeftWidth: 4,
            borderLeftColor: '#ccc',
            paddingLeft: 12,
            marginVertical: 12,
            backgroundColor: '#f9f9f9',
            padding: 12,
            borderRadius: 4,
        },
        div: {
            // Allow custom div styling from HTML
        },
    }), []);

    const handleScroll = useCallback((event: any) => {
        // Optional: Handle scroll events for analytics or custom behavior
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
        // Add your scroll tracking logic here if needed
    }, []);

    if (!htmlContent || htmlContent.trim() === '') {
        return (
            <View style={[styles.emptyContainer]}>
                <Text style={styles.emptyText}>No content available</Text>
            </View>
        );
    }

    return (
        <>
            <HeaderWithBackAndTitle
                headertitle="Plan Details"
                subHeaderTitle=""
                headerStyle={{}}
                navigation={navigation}
                route={route}
                rightIcon={null}
                RightButton={null}
                goingBackHandler={undefined}
                IsRightIconDropDown={undefined}
            />
            <View style={[styles.container]}>
                <ScrollView
                    scrollEnabled={true}
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    bounces={true}
                    decelerationRate="normal"
                    onScroll={handleScroll}
                    scrollEventThrottle={16}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="on-drag"
                >
                    <View style={styles.contentWrapper}>
                        <HTML
                            containerStyle={styles.htmlContainer}
                            staticContentMaxWidth={deviceWidth}
                            html={htmlContent}
                            tagsStyles={tagsStyles}
                            ignoredStyles={['height', 'width']}
                            renderersProps={{
                                img: {
                                    enableExperimentalPercentWidth: true,
                                },
                            }}
                        />
                    </View>
                </ScrollView>
            </View>
        </>
    );
};

const styles = ScaledSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fff',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingBottom: 20, // Add bottom padding for last content
    },
    contentWrapper: {
        paddingHorizontal: 0,
    },
    htmlContainer: {
        flex: 1,
        marginHorizontal: 12,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
    },
    emptyText: {
        fontSize: 16,
        color: '#666',
        textAlign: 'center',
        paddingHorizontal: 20,
    },
});

export default PlanDescription;